import {SupportedLocales} from '#types/locale.js';

export const queueEventsLang = {
  [SupportedLocales.EN]: {
    pages: {
      title: 'Realtime Events',
      subtitle: 'Register a schema for your queued events.',
      loadingAria: 'Loading realtime event',
      notFound: 'Realtime event could not be found.',
      loadError: 'Realtime event settings could not be loaded.',
      tryAgain: 'Try again',
      savingAria: 'Saving realtime event',
      saveError: 'Realtime event could not be saved.',
    },
    schemaEditor: {
      configuration: 'Configuration',
      editor: 'Editor',
      sampleTitle: 'Sample JSON event',
      sampleHelp: 'Generate a JSON Schema from a sample event.',
      samplePlaceholder: 'Paste a sample JSON event here',
      privacy:
        'Use synthetic or redacted samples. Do not include personal data or credentials.',
      generate: 'Generate schema',
      generating: 'Generating…',
      invalidSample: 'Enter a valid JSON object no larger than 100 KB.',
      generationFailed: 'Could not generate a schema. Please try again.',
      invalidResponse:
        'Schema generation returned an invalid response. Please try again.',
      generated: 'Schema generated. Review the changes before saving.',
      stale:
        'Your sample or schema changed during generation. Generate again to use the latest input.',
      original: 'Original',
      updated: 'Updated',
      readOnly: 'Read-only',
      editable: 'Editable',
      noChanges: 'No changes',
      added: 'added',
      removed: 'removed',
      saveHelp: 'Changes are saved only when you select Save.',
    },
    queueEventForm: {
      eventNameLabel: 'Event Name',
      eventNamePlaceholder: 'Enter an event name',
      eventPathLabel: 'Path to Event',
      eventPathPlaceholder: 'Enter a path to the event',
      eventPathTooltip:
        'Please provide the path to your event_type (in a dotted notation such as "properties.event_type") on the JSON event provided below.',
      eventSchemaLabel: 'Register Event Schema',
      eventValueLabel: 'Event Value',
      eventValueTooltip:
        "Please provide the expected value of the event_type you'd like to register (eg, 'purchase').",
      eventValuePlaceholder: 'Enter an event value',
      resetButton: 'Cancel',
      sourceConnectionLabel: 'Event Source',
      sourceConnectionPlaceholder: 'Select an event source',
      sourceConnectionSearch: 'Search event sources',
      submitButton: 'Save',
    },
    queueEventMappingsTable: {
      noMatches: {
        headline: 'There are no dataset group realtime events.',
        caption: "Looks like you haven't added a dataset group realtime event.",
      },
      searchText: 'Search',
    },
    queueEventsTable: {
      noMatches: {
        headline: 'There are no realtime events.',
        caption: "Looks like you haven't added a realtime event.",
      },
      createButton: 'Create a realtime event',
      searchText: 'Search',
    },
    queueEventMappingForm: {
      addFieldButton: 'Add mapping',
      cancelButton: 'Cancel',
      datasetFieldFetchError:
        'Error loading dataset fields. Please refresh the page and try again.',
      datasetFieldLabel: 'Dataset Field',
      datasetFieldPlaceholder: 'Select dataset field',
      realtimeEventFieldFetchError:
        'Error loading realtime event fields. Please refresh the page and try again.',
      legend: 'Map Configuration',
      realtimeEventFieldLabel: 'Realtime Event',
      realtimeEventFieldPlaceholder: 'Select realtime event',
      realtimeEventPlaceholder: 'Select a realtime event',
      realtimeEventEmptyPlaceholder: 'No valid events to map',
      subtitle: 'Tell us how to map your dataset.',
      title: 'Add Realtime Events',
      updateTitle: 'Update Realtime Events',
      submitButton: 'Save',
    },
    queueEventDeleteModal: {
      title: 'Delete Realtime Event',
      description: 'Are you sure you want to delete this realtime event?',
      cancelButton: 'Cancel',
      confirmButton: 'Confirm',
    },
    queueEventMappingDeleteModal: {
      title: 'Delete Dataset Group Realtime Event',
      description:
        'Are you sure you want to delete this dataset group realtime event?',
      cancelButton: 'Cancel',
      confirmButton: 'Confirm',
    },
  },
  [SupportedLocales.ES]: {
    pages: {
      title: 'Eventos en tiempo real',
      subtitle: 'Registre un esquema para sus eventos en cola.',
      loadingAria: 'Cargando evento en tiempo real',
      notFound: 'No se pudo encontrar el evento en tiempo real.',
      loadError:
        'No se pudieron cargar la configuración del evento en tiempo real.',
      tryAgain: 'Intentar de nuevo',
      savingAria: 'Guardando evento en tiempo real',
      saveError: 'No se pudo guardar el evento en tiempo real.',
    },
    schemaEditor: {
      configuration: 'Configuración',
      editor: 'Editor',
      sampleTitle: 'Evento JSON de ejemplo',
      sampleHelp: 'Genera un esquema JSON a partir de un evento de ejemplo.',
      samplePlaceholder: 'Pega un evento JSON de ejemplo aquí',
      privacy:
        'Usa ejemplos sintéticos o anonimizados. No incluyas datos personales ni credenciales.',
      generate: 'Generar esquema',
      generating: 'Generando…',
      invalidSample: 'Introduce un objeto JSON válido de hasta 100 KB.',
      generationFailed: 'No se pudo generar el esquema. Inténtalo de nuevo.',
      invalidResponse:
        'La generación devolvió una respuesta no válida. Inténtalo de nuevo.',
      generated: 'Esquema generado. Revisa los cambios antes de guardar.',
      stale:
        'El ejemplo o esquema cambió durante la generación. Genera de nuevo para usar los datos actuales.',
      original: 'Original',
      updated: 'Actualizado',
      readOnly: 'Solo lectura',
      editable: 'Editable',
      noChanges: 'Sin cambios',
      added: 'añadidas',
      removed: 'eliminadas',
      saveHelp: 'Los cambios solo se guardan al seleccionar Guardar.',
    },
    queueEventForm: {
      eventNameLabel: 'Nombre del evento',
      eventNamePlaceholder: 'Ingrese el nombre del evento',
      eventPathLabel: 'Ruta al evento',
      eventPathPlaceholder: 'Ingrese una ruta al evento',
      eventPathTooltip:
        'Proporcione la ruta a su event_type (en una notación de puntos como "propiedades.event_type") en el evento JSON proporcionado a continuación.',
      eventSchemaLabel: 'Registrar esquema de eventos',
      eventValueLabel: 'Valor del evento',
      eventValueTooltip:
        'Proporcione el valor esperado del event_type que desea registrar (por ejemplo, "compra").',
      eventValuePlaceholder: 'Ingrese un valor de evento',
      resetButton: 'Cancelar',
      sourceConnectionLabel: 'Fuente de eventos',
      sourceConnectionPlaceholder: 'Seleccionar una fuente de eventos',
      sourceConnectionSearch: 'Buscar fuentes de eventos',
      submitButton: 'Guardar',
    },
    queueEventMappingsTable: {
      noMatches: {
        headline:
          'No hay eventos en tiempo real del grupo de conjuntos de datos.',
        caption:
          'Parece que no has agregado un evento en tiempo real del grupo de conjuntos de datos.',
      },
      searchText: 'Buscar',
    },
    queueEventsTable: {
      noMatches: {
        headline: 'No hay eventos en tiempo real.',
        caption: 'Parece que no has agregado un evento en tiempo real.',
      },
      createButton: 'Crear una evento en tiempo real',
      searchText: 'Buscar',
    },
    queueEventMappingForm: {
      addFieldButton: 'Agregar mapeo',
      cancelButton: 'Cancelar',
      datasetFieldFetchError:
        'Error al cargar los campos del conjunto de datos. Por favor, actualiza la página e inténtalo de nuevo.',
      datasetFieldLabel: 'Campo del conjunto de datos',
      datasetFieldPlaceholder: 'Seleccionar campo del conjunto de datos',
      realtimeEventFieldFetchError:
        'Error al cargar los campos del evento en tiempo real. Por favor, actualiza la página e inténtalo de nuevo.',
      legend: 'Configuración de mapeo',
      realtimeEventFieldLabel: 'Evento en tiempo real',
      realtimeEventFieldPlaceholder:
        'Seleccionar campo de evento en tiempo real',
      realtimeEventPlaceholder: 'Seleccionar un evento en tiempo real',
      realtimeEventEmptyPlaceholder: 'No hay eventos válidos para mapear',
      submitButton: 'Guardar',
      subtitle: 'Dinos cómo mapear tu conjunto de datos.',
      title: 'Agregar eventos en tiempo real',
      updateTitle: 'Actualizar eventos en tiempo real',
    },
    queueEventDeleteModal: {
      title: 'Eliminar evento en tiempo real',
      description:
        '¿Estás seguro de que deseas eliminar este evento en tiempo real?',
      cancelButton: 'Cancelar',
      confirmButton: 'Confirmar',
    },
    queueEventMappingDeleteModal: {
      title: 'Eliminar evento en tiempo real del grupo de conjuntos de datos',
      description:
        '¿Estás seguro de que deseas eliminar este evento en tiempo real del grupo de conjuntos de datos?',
      cancelButton: 'Cancelar',
      confirmButton: 'Confirmar',
    },
  },
};
